import React, { useState, useEffect } from 'react';
import { ShieldCheck, X, Key, ShieldAlert, CheckCircle2, Loader2, ArrowRight } from 'lucide-react';
import { db, doc, updateDoc, collection, query, where, getDocs } from '../firebase';

interface ResetPinModalProps {
  isOpen: boolean;
  onClose: () => void;
  token: string;
  email: string;
  onSuccess?: (newPin: string) => void;
}

export const ResetPinModal: React.FC<ResetPinModalProps> = ({
  isOpen,
  onClose,
  token,
  email,
  onSuccess
}) => {
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setNewPin('');
      setConfirmPin('');
      setError('');
      setIsSuccess(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPin.length !== 6 || !/^\d{6}$/.test(newPin)) {
      setError('รหัส PIN ต้องเป็นตัวเลข 6 หลักเท่านั้น');
      return;
    }

    if (newPin !== confirmPin) {
      setError('รหัส PIN ทั้งสองช่องไม่ตรงกัน กรุณาตรวจสอบอีกครั้ง');
      return;
    }

    setLoading(true);

    try {
      // 1. Verify token with Server Endpoint
      const response = await fetch('/api/auth/verify-and-reset-pin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          email,
          newPin
        })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'ลิงก์ตั้งค่า PIN ไม่ถูกต้องหรือหมดอายุ');
      }

      // 2. Also sync to Firestore users collection if user exists
      try {
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('email', '==', email.trim().toLowerCase()));
        const snap = await getDocs(q);
        if (!snap.empty) {
          const userDoc = snap.docs[0];
          await updateDoc(doc(db, 'users', userDoc.id), {
            pinHash: newPin,
            isPinSetup: true,
            updatedAt: new Date().toISOString()
          });
        }
      } catch (dbErr) {
        console.warn('Firestore PIN sync note:', dbErr);
      }

      setIsSuccess(true);
      if (onSuccess) {
        onSuccess(newPin);
      }

      // Auto close after 2.5s
      setTimeout(() => {
        onClose();
      }, 2500);
    } catch (err: any) {
      setError(err?.message || 'เกิดข้อผิดพลาดในการตั้งค่ารหัส PIN');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 backdrop-blur-xs p-4 overflow-y-auto">
      <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl border border-pink-100 p-6 sm:p-8 flex flex-col my-auto animate-fadeIn">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-full transition-colors cursor-pointer"
        >
          <X className="w-5 h-5" />
        </button>

        {isSuccess ? (
          <div className="text-center py-6 space-y-4">
            <div className="w-16 h-16 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto shadow-sm border border-emerald-100">
              <CheckCircle2 className="w-9 h-9" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-900">ตั้งค่ารหัส PIN ใหม่สำเร็จ!</h3>
              <p className="text-xs sm:text-sm text-slate-500 mt-1">
                รหัส PIN 6 หลักของคุณได้รับการอัปเดตอย่างปลอดภัยแล้ว สามารถใช้ยืนยันการสั่งซื้อได้ทันที
              </p>
            </div>
            <button
              onClick={onClose}
              className="mt-4 px-6 py-2.5 bg-gradient-to-r from-[#2A59FE] to-[#3B66FE] text-white rounded-xl text-xs font-bold shadow-md shadow-blue-500/20 cursor-pointer"
            >
              ปิดหน้าต่างนี้
            </button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-blue-100 text-[#2A59FE] mb-3 shadow-2xs">
                <Key className="w-6 h-6" />
              </div>
              <h2 className="text-xl font-black text-slate-900">
                ตั้งค่ารหัส PIN 6 หลักใหม่
              </h2>
              <p className="text-xs text-slate-500 mt-1">
                สำหรับบัญชีอีเมล <span className="font-bold text-[#2A59FE]">{email}</span>
              </p>
            </div>

            {error && (
              <div className="mb-4 p-3.5 bg-rose-50 border border-rose-200 rounded-2xl flex items-start gap-2.5 text-xs text-rose-700">
                <ShieldAlert className="w-4 h-4 shrink-0 text-rose-600 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-[11px] font-bold text-slate-700 mb-1">
                  กำหนดรหัส PIN 6 หลักใหม่
                </label>
                <input
                  type="password"
                  maxLength={6}
                  required
                  autoFocus
                  placeholder="••••••"
                  value={newPin}
                  onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))}
                  className="w-full text-center tracking-widest text-2xl py-3 rounded-xl border border-slate-200 focus:border-[#2A59FE] focus:ring-2 focus:ring-blue-500/15 focus:outline-none bg-slate-50/60 font-mono font-bold"
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-700 mb-1">
                  ยืนยันรหัส PIN 6 หลักอีกครั้ง
                </label>
                <input
                  type="password"
                  maxLength={6}
                  required
                  placeholder="••••••"
                  value={confirmPin}
                  onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ''))}
                  className="w-full text-center tracking-widest text-2xl py-3 rounded-xl border border-slate-200 focus:border-[#2A59FE] focus:ring-2 focus:ring-blue-500/15 focus:outline-none bg-slate-50/60 font-mono font-bold"
                />
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={loading || newPin.length !== 6 || confirmPin.length !== 6}
                  className="w-full py-3.5 px-4 bg-gradient-to-r from-[#2A59FE] via-[#3B66FE] to-[#4F46E5] hover:opacity-95 text-white rounded-xl text-sm font-bold flex items-center justify-center gap-2 shadow-md shadow-blue-500/25 transition-all active:scale-[0.98] disabled:opacity-50 cursor-pointer"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      กำลังบันทึกรหัส PIN ใหม่...
                    </>
                  ) : (
                    <>
                      <ShieldCheck className="w-4 h-4" />
                      บันทึกรหัส PIN ใหม่
                    </>
                  )}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};
